/**
 * useMathCoachSession hook
 * React Query hook for fetching the active math coach session with polling and timeout.
 *
 * Two consumers:
 *   - Dashboard button: one-time check on mount, no polling
 *   - MathCoachGuidanceScreen: polls every 5s for up to 2 minutes, auto-navigates on detection
 */

import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef, useCallback } from 'react';
import { getActiveMathCoachSession } from '@/services/mathCoach';
import type { ActiveMathCoachSession } from '@/services/mathCoach';

const POLL_INTERVAL_MS = 5000; // 5 seconds
const POLL_TIMEOUT_MS = 120000; // 2 minutes

export interface UseMathCoachSessionReturn {
  /** Active session data (null if 404 / no session) */
  session: ActiveMathCoachSession | null;
  /** True during initial fetch */
  isLoading: boolean;
  /** Error object (non-404 errors only) */
  error: Error | null;
  /** Whether polling is currently active */
  isPolling: boolean;
  /** Whether polling has timed out (2 minutes elapsed) */
  isTimedOut: boolean;
  /** Start polling (called by guidance screen) */
  startPolling: () => void;
  /** Stop polling */
  stopPolling: () => void;
}

export function useMathCoachSession(): UseMathCoachSessionReturn {
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable stopPolling reference (no deps that change)
  const stopPolling = useCallback(() => {
    setPollingEnabled(false);
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Stable startPolling reference guarded against concurrent calls
  const startPolling = useCallback(() => {
    // If already polling (not timed out), do nothing — prevents multiple concurrent loops
    setPollingEnabled((current) => {
      if (current) return current; // already polling, no change
      return current; // will be set to true below after guard check
    });

    setIsTimedOut((currentTimedOut) => {
      // Only start if not already polling-in-progress without timeout
      // We use the functional form to read current state atomically
      setPollingEnabled((currentPolling) => {
        if (currentPolling && !currentTimedOut) {
          // Already polling and not timed out — do nothing
          return currentPolling;
        }

        // Clear any existing timeout timer before starting a new one
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }

        // Start the 2-minute timeout
        timeoutRef.current = setTimeout(() => {
          setIsTimedOut(true);
          setPollingEnabled(false);
          timeoutRef.current = null;
        }, POLL_TIMEOUT_MS);

        return true; // enable polling
      });

      return false; // reset isTimedOut to false on startPolling
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery<ActiveMathCoachSession | null, Error>({
    queryKey: ['math-coach', 'active'],
    queryFn: async () => {
      try {
        return await getActiveMathCoachSession();
      } catch (err: unknown) {
        // 404 = no active session — this is a normal state, not an error
        // The Axios interceptor maps HTTP 404 to { code: 'NOT_FOUND', message: '...' }
        // using data?.error from the backend response body.
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code: string }).code === 'NOT_FOUND'
        ) {
          return null;
        }
        // Re-throw all other errors (500, network, etc.)
        throw err as Error;
      }
    },
    refetchInterval: pollingEnabled && !isTimedOut ? POLL_INTERVAL_MS : false,
    retry: false, // 404 is expected; other errors should surface immediately
    staleTime: 0, // always refetch to get latest session state
  });

  // Derive session value: undefined (loading) -> null (not started), object (found)
  const session = data === undefined ? null : data;

  return {
    session,
    isLoading,
    error: queryError,
    isPolling: pollingEnabled && !isTimedOut,
    isTimedOut,
    startPolling,
    stopPolling,
  };
}
